import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import apiAxiosInstance from "../../common/utilities/axios/apiAxiosInstance";
import microsoftGraphAxiosInstance from "../../common/utilities/axios/microsoftGraphAxiosInstance";
import sleep from "sleep-promise";

export const useGetAllAdminUserListQuery = () => {
  return useQuery({
    queryKey: ["getAllAdminUserListQuery"],
    queryFn: () => {
      // sleep(2000);
      return apiAxiosInstance.get(`/admin/Users`).then((res) => {
        return res.data;
      });
    },
  });
};

export const useGetCountryListOnlyForRightTractByLocaleQuery = () => {
  return useQuery({
    queryKey: ["getCountryListByLocaleQuery"],
    queryFn: () => {
      // sleep(2000);

      const getLocale = "en-US";
      return apiAxiosInstance
        .get(`/references/references/data/${getLocale}/country`)
        .then((res) => {
          /**transforming response */
          /**timezonesListForAdminUser[0]?.items */
          let result = [];

          if (!!res.data[0]) {
            const countryList = res.data[0].items;

            !!countryList &&
              countryList.forEach((eachCountryObj) => {
                const standardCodes = eachCountryObj?.standardCodes;
                standardCodes.forEach((eachStandardCodeObj) => {
                  /**get only countries where itemCodeStandardName==="rt"*/
                  if (eachStandardCodeObj?.itemCodeStandardName === "rt") {
                    result.push(eachCountryObj);
                  }
                });
              });
          }
          return !!result && result.length ? result : [];
        });
    },
  });
};
export const useGetTimeZoneListForAdminUserQuery = () => {
  return useQuery({
    queryKey: ["getTimeZoneListForAdminUserQuery"],
    queryFn: () => {
      // sleep(2000);
      return apiAxiosInstance
        .get(`/references/references/data/en-US/timezone`)
        .then((res) => {
          /**transforming response */
          /**timezonesListForAdminUser[0]?.items */
          return !!res.data[0] ? res.data[0].items : [];
        });
    },
  });
};

export const useGetCountrySpecificLanguageListByCountryCodeQuery = ({
  countryCode,
}) => {
  return useQuery({
    queryKey: ["getCountrySpecificLanguageListByCountryCodeQuery", countryCode],
    queryFn: () => {
      // sleep(2000);
      return apiAxiosInstance
        .get(`/admin/Languages/${countryCode}`)
        .then((res) => {
          return res.data;
        });
    },
    // The query will not execute until the countryCode exists
    enabled: !!countryCode,
  });
};

/**countryCodes is an array */
export const useGetServiceOfficeListByCountryCodesQuery = ({
  countryCodes,
}) => {
  return useQuery({
    queryKey: ["getServiceOfficeListByCountryCodesQuery", countryCodes],
    queryFn: () => {
      // sleep(2000);

      /**countryCodes are passed as parames e.g. us,ca,in. We are taking array arg and convering to string */
      // const countryCodeArrayToString = !!countryCodes && countryCodes.length ? countryCodes.toString() : ""
      return apiAxiosInstance
        .get(`/references/ServiceOffice?countrycode=${countryCodes}`)
        .then((res) => {
          return res.data;
        });
    },
    // The query will not execute until the countryCodes exists
    enabled: !!countryCodes,
  });
};

export const useGetCronofyTimeZoneDataByCountryCodeQuery = ({
  countryCode,
}) => {
  return useQuery({
    queryKey: ["getCronofyTimeZoneDataByCountryCodeQuery", countryCode],
    queryFn: () => {
      // sleep(2000);

      return apiAxiosInstance
        .get(`/admin/IANATimeZone/${countryCode}/Cronofy`)
        .then((res) => {
          return res.data;
        });
    },
    // The query will not execute until the countryCode exists
    enabled: !!countryCode,
  });
};
export const useGetCronofyServiceAccountDataQuery = () => {
  return useQuery({
    queryKey: ["getCronofyServiceAccountDataQuery"],
    queryFn: () => {
      // sleep(2000);

      return apiAxiosInstance
        .get(`/admin/ServiceAccount/Cronofy`)
        .then((res) => {
          return res.data;
        });
    },
  });
};

export const useGetAdminUserByIdQuery = ({ adminUserId }) => {
  return useQuery({
    queryKey: ["getAdminUserByIdQuery", adminUserId],
    queryFn: () => {
      // sleep(2000);

      return apiAxiosInstance
        .get(`/admin/UserDetailsById/${adminUserId}`)
        .then((res) => {
          return res.data;
        });
    },
    // The query will not execute until the adminUserId exists
    enabled: !!adminUserId,
  });
};

/**veryfy active user using emailId via microsoft graph API */
export const useVerifyActiveAdminUserEmailIdFromADQuery = ({ emailId }) => {
  const [retryEmail, setRetryEmail] = useState("");
  const [hasRetried, setHasRetried] = useState(false); // state for calling the api only once
  const finalEmailId = retryEmail || emailId;

  useEffect(() => {
    // Reset retry logic when emailId changes
    setRetryEmail("");
    setHasRetried(false);
  }, [emailId]);

  return useQuery({
    queryKey: ["verifyActiveAdminUserEmailIdFromADQuery", finalEmailId],
    queryFn: async () => {
      try {
        const encodedEmailId = encodeURIComponent(finalEmailId);
        const response = await microsoftGraphAxiosInstance.get(
          `/v1.0/users/${encodedEmailId}`
        );
        return response.data;
      } catch (error) {
        const graphAPIErrorCode = error?.response?.data?.error?.code;

        if (graphAPIErrorCode === "Request_ResourceNotFound" && !hasRetried) {
          const emailFormatted = emailId.replace(/@/g, "_");
          const guestUPN = `${emailFormatted}#EXT#@${process.env.REACT_APP_GUEST_UPN}`;

          setRetryEmail(guestUPN); // Update the retry email
          setHasRetried(true); // Mark retry as done
          throw error;
        } else {
          throw error;
        }
      }
    },
    retry: false, // Prevent automatic retries by React Query
    enabled: !!finalEmailId, // Ensure the query runs only when finalEmailId exists
  });
};

/** MUTATIONS */

// api for create client user
export const useCreateAdminUserMutation = () => {
  return useMutation({
    mutationFn: ({ body }) => {
      return apiAxiosInstance.post(`/admin/UserDetail`, body, {
        timeout: 300000 /**5min */,
      });
    },
  });
};
export const useUpdateAdminUserMutation = () => {
  return useMutation({
    mutationFn: ({ body }) => {
      return apiAxiosInstance.put(`/admin/UserDetail`, body, {
        timeout: 300000 /**5min */,
      });
    },
  });
};
